import { articleUnitSchema, type ArticleUnit } from '../inventory/article.schema';
import { MAX_BOM_DEPTH, type BomItemType } from './bom-tree';
import type { ChainCalcResult } from './chain-calc';
import type { EngineeringBomItemDraft } from './bom.schema';

/**
 * Stücklisten-Vorlage (VB-26025 Stufe B) — der Katalog, der im Excel das Blatt
 * „Haupteingabe" ist.
 *
 * Die Vorlage ist **kein leeres Blatt**: 250 Kandidatenzeilen, davon 206 mit
 * fertiger Artikelnummer, Bezeichnung, Einheit, Werkstoff und Lieferant. Der
 * Konstrukteur trägt **nur Mengen** ein; Zeilen ohne Menge fallen heraus. Genau
 * so arbeitet das Blatt heute (`=IF(C3=0,"",H3)`), und genau das macht die
 * Stückliste „zu 90 % fertig", bevor jemand etwas tippt.
 *
 * Vier Arten, wie eine Menge zustande kommt — alle vier stehen im echten Blatt:
 * - `MANUAL` (215 Zeilen): von Hand
 * - `LENGTH_TIMES_COUNT` (20): Stück × Länge ÷ 1000 → Meterware
 * - `CHAIN_CALC` (3): direkt aus der Kettenberechnung
 * - `DERIVED` (12): Mengen anderer Zeilen mal einem Faktor („zwei Füllblöcke je
 *   Balken")
 *
 * **Stücklisten-Blöcke (ADR 0097).** Zeilen mit demselben `assemblyKey` bilden
 * einen Block — die Baugruppe der Projektauslegung, zu der sie gehören („1.
 * Hauptwelle", „3. Balken" …). Ein Block **passt sich an**: Er gilt als aktiv,
 * sobald die Baugruppe in der Auslegung gefüllt ist, sonst klappt er im
 * Füll-Dialog ein (bleibt aber von Hand aufklappbar). Zeilen mit
 * `layoutTemplateKey` gehen dabei einen Schritt weiter als `LAYOUT`: Sie
 * übernehmen nicht nur die MENGE aus der Auslegung, sondern auch den ARTIKEL —
 * die Hauptwelle ist im Katalog ein Rohr `ø306`, kann aber je nach Beckenbreite
 * ein anderes sein. Wo die Auslegung noch keinen Artikel gewählt hat, bleibt
 * die Zeile ohne Artikel und wird gemeldet, nicht geraten.
 */

/** Woher die Menge einer Vorlagenzeile kommt. */
export const BOM_QUANTITY_SOURCES = ['MANUAL', 'LENGTH_TIMES_COUNT', 'CHAIN_CALC', 'DERIVED', 'LAYOUT'] as const;
export type BomQuantitySource = (typeof BOM_QUANTITY_SOURCES)[number];

/**
 * Werte der Projektauslegung, aus denen eine Menge kommen kann — geschlossene
 * Weißliste, genau wie bei der Kettenberechnung. Ein offener Feldzugriff auf 44
 * Vorlagenzeilen wäre bequem und würde beim ersten umbenannten Schlüssel still
 * eine Menge von 0 liefern.
 */
export const LAYOUT_FIELDS = [
  /** Anzahl Räumer (Abschnitt „Grundauslegung") — treibt z. B. „2 Sensoren je Räumer". */
  'scraperCount',
  /** Balken je Räumer — die Stückzahl der Zeile „Balken" (ADR 0040: Stück + mm je Stück). */
  'flightCount',
  /** Länge je Balken in mm — die Ablängung derselben Zeile. */
  'flightLengthMm',
  /** Beckenanzahl und Beckenmaße (stehen an den Projektdaten). */
  'basinCount',
  'lengthM',
  'widthM',
  'heightM',
  'waterLevelM',
] as const;
export type LayoutField = (typeof LAYOUT_FIELDS)[number];

/** Deutsche Namen der Auslegungswerte — für Hinweise und die Pflege-Maske. */
export const LAYOUT_FIELD_LABELS: Record<LayoutField, string> = {
  scraperCount: 'Anzahl Räumer',
  flightCount: 'Balken je Räumer (Stück)',
  flightLengthMm: 'Länge je Balken (mm)',
  basinCount: 'Beckenanzahl',
  lengthM: 'Beckenlänge (m)',
  widthM: 'Beckenbreite (m)',
  heightM: 'Beckenhöhe (m)',
  waterLevelM: 'Höhe Wasserspiegel (m)',
};

/**
 * Baugruppe der Projektauslegung, an die eine Vorlagenzeile gekoppelt sein kann
 * (ADR 0097) — die neun nummerierten Abschnitte der Auslegung
 * (`LAYOUT_SECTIONS` in `packages/shared/src/projects`), als eigene,
 * geschlossene Weißliste hier: dieselbe Begründung wie bei `LAYOUT_FIELDS` —
 * ein offener Textbezug würde beim ersten umbenannten Abschnitt still ins Leere
 * laufen. Die Zuordnung Schlüssel → Abschnitt pflegt die Technik im Kopf, nicht
 * der Code (kein Import zwischen `engineering` und `projects`, §5).
 */
export const ASSEMBLY_KEYS = [
  'hauptwelle',
  'antriebseinheit',
  'balken',
  'foerderkette',
  'umlenkstummel',
  'ruecklaufschiene',
  'bodengleitstreifen',
  'gleichlaufueberwachung',
  'skimrinne',
] as const;
export type AssemblyKey = (typeof ASSEMBLY_KEYS)[number];

export const ASSEMBLY_KEY_LABELS: Record<AssemblyKey, string> = {
  hauptwelle: '1. Hauptwelle',
  antriebseinheit: '2. Antriebseinheit',
  balken: '3. Balken',
  foerderkette: '4. Förderkette',
  umlenkstummel: '5. Umlenkstummel',
  ruecklaufschiene: '6. Rücklaufschiene',
  bodengleitstreifen: '7. Bodengleitstreifen',
  gleichlaufueberwachung: '8. Gleichlaufüberwachung',
  skimrinne: '9. Skimrinne',
};

/**
 * Die Blöcke, die eine Vorlage tatsächlich anbietet — für die Baukasten-Auswahl
 * im Stücklisten-Wizard (ADR 0098). In der festen Reihenfolge der Weißliste,
 * nicht in der Reihenfolge, in der die Zeilen zufällig stehen — sonst sprängen
 * die Kästchen bei jeder neuen Vorlage anders herum.
 */
export function listAssemblyKeys(rows: readonly { assemblyKey?: AssemblyKey | null }[]): AssemblyKey[] {
  const present = new Set(rows.map((row) => row.assemblyKey).filter((key): key is AssemblyKey => Boolean(key)));
  return ASSEMBLY_KEYS.filter((key) => present.has(key));
}

/**
 * Eine Auslegungszeile, wie eine Vorlagenzeile sie über `layoutTemplateKey`
 * gezielt anspricht (ADR 0097) — mit Artikel, nicht nur mit Menge: Anders als
 * `byArticleId` (zufällige Übereinstimmung) ist das ein Verweis auf eine
 * bestimmte ROLLE der Auslegung („die Hauptwelle"), deren Artikel je Projekt
 * ein anderer sein darf (Rohr ø88,9 bis ø306, je nach Beckenbreite).
 */
export interface LayoutRowArticleLike {
  articleId: string | null;
  articleNumber: string | null;
  articleName: string | null;
  unit: string | null;
  quantity: number | null;
  cutLengthMm: number | null;
}

/**
 * Die Auslegung, so wie das Füllen sie braucht.
 *
 * `byArticleId` ist der zweite, stärkere Weg: Was in der Auslegung als **Artikel**
 * gewählt ist, ist in der Stückliste schon vorbelegt — ohne dass jemand die
 * Vorlagenzeile dafür konfigurieren muss. Der Abgleich läuft über die Artikel-Id,
 * und die ist in beiden Welten dieselbe.
 *
 * `byTemplateKey` ist der dritte, gezielte Weg (ADR 0097): eine Vorlagenzeile
 * benennt über `layoutTemplateKey`, WELCHE Auslegungszeile sie meint — Menge
 * **und** Artikel folgen dann von dort, nicht nur die Menge.
 */
export interface BomLayoutInput {
  values: Partial<Record<LayoutField, number | null>>;
  byArticleId?: ReadonlyMap<string, { quantity: number | null; cutLengthMm: number | null }>;
  byTemplateKey?: ReadonlyMap<string, LayoutRowArticleLike>;
}

/** Feld der Kettenberechnung, aus dem eine Menge kommen kann. */
export const CHAIN_CALC_FIELDS = ['conveyorChainLengthM', 'driveChainLengthM'] as const;
export type ChainCalcField = (typeof CHAIN_CALC_FIELDS)[number];

/** Welche Zahl einer Quellzeile in eine abgeleitete Menge eingeht. */
export const BOM_DERIVED_FIELDS = ['QUANTITY', 'COUNT'] as const;
export type BomDerivedField = (typeof BOM_DERIVED_FIELDS)[number];

/** Eine Kandidatenzeile der Vorlage. */
export interface BomTemplateRowLike {
  /** Stabile Reihenfolge — im Blatt die Zeilennummer. */
  position: number;
  /** Abschnitt („Kunststoffkomponenten", „Stahlteile" …). */
  section: string;
  type: BomItemType;
  articleId: string | null;
  articleNumber: string | null;
  description: string;
  unit: ArticleUnit | null;
  groupCode: string | null;
  materialText: string | null;
  supplierName: string | null;
  /** Länge je Stück in mm (Meterware). */
  defaultLengthMm: number | null;
  quantitySource: BomQuantitySource;
  /** Bei `CHAIN_CALC`: welches Feld. */
  chainField: ChainCalcField | null;
  /** Bei `LAYOUT`: welcher Wert der Projektauslegung (mal `derivedFactor`). */
  layoutField?: LayoutField | null;
  /**
   * Baugruppe der Auslegung, zu der diese Zeile gehört (ADR 0097). Zeilen mit
   * demselben Schlüssel bilden einen **Block**: Er gilt als aktiv, sobald die
   * Baugruppe in der Auslegung etwas trägt, und klappt sonst im Füll-Dialog ein
   * (bleibt aber von Hand aufklappbar — ADR 0094, Eingabe schlägt Auslegung).
   */
  assemblyKey?: AssemblyKey | null;
  /**
   * Auslegungszeile, deren ARTIKEL (und, wo keine eigene Eingabe/kein `LAYOUT`
   * greift, auch Menge) diese Vorlagenzeile übernimmt (ADR 0097) — der Schlüssel
   * ist derselbe `templateKey` wie in `ProjectLayoutRow`. Anders als der eigene
   * `articleId` der Zeile ist das kein fester Artikel, sondern eine Rolle: Was
   * in der Auslegung dafür gewählt wurde, gilt.
   */
  layoutTemplateKey?: string | null;
  /**
   * Position der übergeordneten Zeile — Artikel eingerückt unter Artikel
   * (ADR 0099), z. B. „Motor" unter „Getriebemotor". `null`/fehlend = oberste
   * Ebene. Dieselbe Positions-Referenz wie `derivedFromPositions`, keine
   * eigene Kennung.
   */
  parentPosition?: number | null;
  /** Bei `DERIVED`: Positionen der Quellzeilen, ihr Feld und der Faktor. */
  derivedFromPositions: number[];
  derivedField: BomDerivedField | null;
  derivedFactor: number;
  note: string | null;
}

/** Was der Konstrukteur je Zeile eingetragen hat. */
export interface BomTemplateEntry {
  /** Menge (bei `MANUAL`). */
  quantity?: number | null;
  /** Stück (bei `LENGTH_TIMES_COUNT` und als Quelle für `DERIVED`). */
  count?: number | null;
  /** Abweichende Länge in mm; sonst gilt `defaultLengthMm`. */
  lengthMm?: number | null;
}

export interface BomTemplateFillResult {
  /** Fertige Entwurfszeilen — dieselbe Nutzlast, die das Speichern erwartet. */
  items: EngineeringBomItemDraft[];
  /** Zeilen, die eine Menge hätten, deren Quelle aber fehlt. */
  issues: string[];
  /** Wie viele Kandidatenzeilen übernommen wurden bzw. leer blieben. */
  summary: { taken: number; skipped: number; sections: number };
}

/** Auf vier Stellen runden — dieselbe Genauigkeit wie die Kettenberechnung. */
function round(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/**
 * Werte der Kettenberechnung, die in einem Zeilentext stehen dürfen.
 *
 * Im Blatt sind drei Zusatztexte **gerechnet**, nicht getippt — etwa
 * `='KETTEN - Berechnungen'!B30*2*…!B9 & " Stränge zu je " & …!C30-1 & " …"`,
 * was gedruckt „8 Stränge zu je 32 Kettenglieder + 1 Mitnehmer + 1 Bolzen"
 * ergibt. Diese Zahlen gehören zum Projekt, nicht zur Vorlage: als fester Text
 * gespeichert stünden sie ab dem nächsten Projekt falsch in der Stückliste.
 * Deshalb tragen solche Zeilen Platzhalter `{{chain.…}}`, die hier eingesetzt
 * werden — mit einer Weißliste, damit kein beliebiger Feldzugriff entsteht.
 */
export function chainPlaceholderValues(chain: ChainCalcResult): Record<string, number> {
  const [shortRow, longRow] = chain.rows;
  return {
    'chain.totalLinks': chain.totalLinks,
    'chain.conveyorChainLengthM': chain.conveyorChainLengthM,
    'chain.driveChainLengthM': chain.driveChain.lengthM,
    'chain.driveChainLinks': chain.driveChain.linksPerDrive,
    'chain.rows.0.flights': shortRow.flights,
    'chain.rows.0.links': shortRow.links,
    'chain.rows.0.strands': shortRow.strands,
    'chain.rows.0.linksPerStrand': shortRow.linksPerStrand,
    'chain.rows.1.flights': longRow.flights,
    'chain.rows.1.links': longRow.links,
    'chain.rows.1.strands': longRow.strands,
    'chain.rows.1.linksPerStrand': longRow.linksPerStrand,
    'chain.spare.rolls': chain.spare.rolls,
    'chain.spare.links': chain.spare.links,
    'chain.spare.lengthM': chain.spare.lengthM,
  };
}

const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;

/**
 * Enthält der Text einen Platzhalter? Eigener, nicht-globaler Ausdruck: `test`
 * mit `/g` merkt sich die Fundstelle und liefert beim zweiten Aufruf `false`.
 */
export function hasChainPlaceholder(text: string | null | undefined): boolean {
  return Boolean(text) && /\{\{\s*[\w.]+\s*\}\}/.test(text as string);
}

/**
 * Platzhalter einsetzen. Ohne Kettenberechnung oder bei unbekanntem Namen bleibt
 * der Platzhalter stehen und wird gemeldet — ein halb gefüllter Text ist besser
 * als eine erfundene Zahl, und im Bericht steht, warum.
 */
function fillPlaceholders(text: string, chain: ChainCalcResult | null, issues: string[]): string {
  const values = chain ? chainPlaceholderValues(chain) : null;
  return text.replace(PLACEHOLDER, (match, name: string) => {
    if (!values) {
      issues.push(`„${text}": der Text kommt aus der Kettenberechnung — die fehlt noch.`);
      return match;
    }
    const value = values[name];
    if (value === undefined) {
      issues.push(`„${text}": ${name} ist kein Wert der Kettenberechnung.`);
      return match;
    }
    // Deutsche Schreibweise, ohne Tausenderpunkt: der Text steht in einer Zeile
    // wie „8 Stränge zu je 32 Kettenglieder".
    return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
  });
}

/**
 * Vorlage + Eingaben → Entwurfszeilen.
 *
 * Regeln, alle aus dem Blatt:
 * - Eine Position ohne Menge fällt heraus (`=IF(C3=0,"",…)`).
 * - Eine Abschnitts-Überschrift bleibt nur, wenn in ihrem Abschnitt mindestens
 *   eine Position übrig ist — sonst stünde im Beleg eine Überschrift ohne Inhalt.
 * - Ein Zusatztext gehört zur Position darüber und fällt mit ihr weg.
 *
 * **Hierarchie (ADR 0099):** Trägt eine Zeile `parentPosition`, hängt sie im
 * Entwurf unter ihrer Elternzeile — Artikel eingerückt unter Artikel, genau
 * wie im fertigen Stücklisten-Baum. Überlebt die Elternzeile nicht (keine
 * Menge) oder bildet die Vorlage einen Zirkel, rückt die Zeile an die oberste
 * Ebene — Material verschwindet nicht still.
 */
export function buildBomFromTemplate(
  rows: readonly BomTemplateRowLike[],
  entries: ReadonlyMap<number, BomTemplateEntry>,
  chain: ChainCalcResult | null = null,
  layout: BomLayoutInput | null = null,
): BomTemplateFillResult {
  const issues: string[] = [];
  const ordered = [...rows].sort((a, b) => a.position - b.position);
  const byPosition = new Map(ordered.map((row) => [row.position, row]));

  /**
   * Menge einer Zeile. Abgeleitete Zeilen greifen auf andere Zeilen zu, deshalb
   * rekursiv mit Merkzettel — und mit Zyklenwächter: `A = B×2, B = A×2` ist in
   * einer pflegbaren Vorlage möglich und darf nicht in einen Stapelüberlauf
   * laufen, sondern muss als Fehler herauskommen.
   */
  const quantities = new Map<number, number | null>();
  const resolving = new Set<number>();

  const quantityOf = (row: BomTemplateRowLike): number | null => {
    if (quantities.has(row.position)) return quantities.get(row.position) ?? null;
    if (row.type !== 'POSITION') {
      quantities.set(row.position, null);
      return null;
    }
    if (resolving.has(row.position)) {
      issues.push(`${row.description}: die Ableitung verweist im Kreis auf sich selbst.`);
      return null;
    }
    resolving.add(row.position);
    const value = compute(row);
    resolving.delete(row.position);
    quantities.set(row.position, value);
    return value;
  };

  /** Die Auslegungszeile, die `layoutTemplateKey` benennt — falls gesetzt. */
  const layoutRowOf = (row: BomTemplateRowLike) =>
    row.layoutTemplateKey ? layout?.byTemplateKey?.get(row.layoutTemplateKey) ?? null : null;

  /**
   * Was die Auslegung zu DIESER Zeile sagt. Der gezielte Verweis
   * (`layoutTemplateKey`, ADR 0097) geht vor: Er gilt auch dann, wenn der
   * gewählte Artikel dort noch nie mit dieser Vorlagenzeile übereinstimmen kann
   * (ein Artikel-Bezug wäre bei wechselndem Artikel — z. B. der Hauptwelle je
   * nach Beckenbreite — immer zufällig). Ohne Verweis gilt weiter die
   * Artikel-Id-Übereinstimmung (ADR 0094).
   */
  const layoutAmountOf = (row: BomTemplateRowLike) => {
    const byRow = layoutRowOf(row);
    if (byRow) return byRow;
    return row.articleId ? layout?.byArticleId?.get(row.articleId) ?? null : null;
  };

  const compute = (row: BomTemplateRowLike): number | null => {
    const entry = entries.get(row.position);
    const fromLayout = layoutAmountOf(row);
    switch (row.quantitySource) {
      case 'MANUAL':
        // Eingetragene Menge schlägt die Auslegung — wer hier etwas tippt, hat
        // einen Grund. Ohne Eingabe gilt, was in der Auslegung steht.
        return entry?.quantity ?? fromLayout?.quantity ?? null;

      case 'LENGTH_TIMES_COUNT': {
        const count = entry?.count ?? fromLayout?.quantity ?? null;
        if (!count) return null;
        const lengthMm = entry?.lengthMm ?? fromLayout?.cutLengthMm ?? row.defaultLengthMm;
        if (!lengthMm) {
          issues.push(`${row.description}: Stück ohne Länge — Meterware braucht beides.`);
          return null;
        }
        // Meterware: Stück × Länge in mm ⇒ Meter. Genau die Formel des Blattes.
        return round((count * lengthMm) / 1000);
      }

      case 'CHAIN_CALC': {
        if (!chain) {
          issues.push(`${row.description}: Menge käme aus der Kettenberechnung — die fehlt noch.`);
          return null;
        }
        if (row.chainField === 'conveyorChainLengthM') return chain.conveyorChainLengthM;
        if (row.chainField === 'driveChainLengthM') return chain.driveChain.lengthM;
        issues.push(`${row.description}: unbekanntes Feld der Kettenberechnung.`);
        return null;
      }

      case 'LAYOUT': {
        if (!row.layoutField) {
          issues.push(`${row.description}: unbekannter Wert der Projektauslegung.`);
          return null;
        }
        const value = layout?.values?.[row.layoutField] ?? null;
        if (value == null) {
          issues.push(
            `${row.description}: Menge käme aus der Projektauslegung (${LAYOUT_FIELD_LABELS[row.layoutField]}) — ` +
              'dort steht noch nichts.',
          );
          return null;
        }
        const quantity = round(value * row.derivedFactor);
        return quantity > 0 ? quantity : null;
      }

      case 'DERIVED': {
        // Summe der Quellzeilen × Faktor („zwei Füllblöcke je Balken"). `COUNT`
        // nimmt die eingetragene Stückzahl, `QUANTITY` die **gerechnete** Menge
        // der Quellzeile — im Blatt zeigt die Formel auf genau diese Zellen.
        let sum = 0;
        for (const position of row.derivedFromPositions) {
          const source = byPosition.get(position);
          if (!source) {
            issues.push(`${row.description}: die Vorlage verweist auf Zeile ${position}, die es nicht gibt.`);
            continue;
          }
          if (row.derivedField === 'COUNT') {
            sum += entries.get(position)?.count ?? 0;
            continue;
          }
          sum += quantityOf(source) ?? 0;
        }
        const quantity = round(sum * row.derivedFactor);
        return quantity > 0 ? quantity : null;
      }

      default:
        return null;
    }
  };

  for (const row of ordered) quantityOf(row);

  /** Welche Zeile überlebt? */
  const survives = new Map<number, boolean>();
  let lastPositionSurvived = false;
  for (const row of ordered) {
    if (row.type === 'POSITION') {
      const quantity = quantities.get(row.position);
      lastPositionSurvived = quantity != null && quantity > 0;
      survives.set(row.position, lastPositionSurvived);
      continue;
    }
    if (row.type === 'NOTE') {
      // Der Zusatztext hängt an der Position darüber.
      survives.set(row.position, lastPositionSurvived);
      continue;
    }
    // Abschnitt: erst im nächsten Schritt, wenn klar ist, was darunter bleibt.
    survives.set(row.position, false);
    lastPositionSurvived = false;
  }

  /** Überschriften, deren Abschnitt etwas enthält. */
  const sectionsWithContent = new Set<string>();
  for (const row of ordered) {
    if (row.type === 'POSITION' && survives.get(row.position)) sectionsWithContent.add(row.section);
  }
  for (const row of ordered) {
    if (row.type === 'SECTION' && sectionsWithContent.has(row.section)) survives.set(row.position, true);
  }

  /**
   * Übergeordnete Zeile im Entwurf (ADR 0099) — `null`, wenn es keine gibt,
   * die Elternzeile selbst nicht übernommen wird (Position ohne Menge fällt
   * heraus, das Kind rückt dann an die oberste Ebene) oder die Vorlage einen
   * Zirkel enthält. Ein Zirkel bricht hier ab, statt die Vorschau zu
   * verschlucken — die Vorlagenpflege müsste ihn ohnehin von Hand beheben.
   */
  const parentKeyOf = (row: BomTemplateRowLike): string | null => {
    const parentPosition = row.parentPosition;
    if (parentPosition == null) return null;
    const seen = new Set<number>([row.position]);
    let currentPosition: number | null = parentPosition;
    let depth = 0;
    while (currentPosition != null) {
      if (seen.has(currentPosition) || depth >= MAX_BOM_DEPTH) {
        issues.push(`${row.description}: übergeordnete Zeilen bilden einen Zirkel oder sind zu tief verschachtelt.`);
        return null;
      }
      seen.add(currentPosition);
      depth += 1;
      currentPosition = byPosition.get(currentPosition)?.parentPosition ?? null;
    }
    if (!survives.get(parentPosition)) {
      issues.push(
        `${row.description}: die übergeordnete Zeile fällt heraus (keine Menge) — die Zeile steht jetzt oben.`,
      );
      return null;
    }
    return `vorlage-${parentPosition}`;
  };

  const items: EngineeringBomItemDraft[] = [];
  for (const row of ordered) {
    if (!survives.get(row.position)) continue;
    const isPosition = row.type === 'POSITION';
    const entry = entries.get(row.position);
    // Dieselbe Auflösung wie beim Rechnen: Eingabe, sonst Auslegung, sonst Vorlage.
    // Zwei Stellen mit unterschiedlicher Reihenfolge wären genau die Abweichung,
    // die niemandem auffällt, bis die Bemerkung nicht zur Menge passt.
    const fromLayout = layoutAmountOf(row);
    const count = entry?.count ?? fromLayout?.quantity ?? null;
    const lengthMm = entry?.lengthMm ?? fromLayout?.cutLengthMm ?? row.defaultLengthMm;
    /**
     * Bei Meterware steht im Blatt in der Bemerkung „12 Stück á 5920 mm"
     * (Spalte K, `=J17 & " Stück á " & I17 & " mm"`). Die Zahl gehört mit in die
     * Stückliste: Wer 71,04 m bestellt, muss wissen, in welchen Längen.
     */
    const cutNote =
      isPosition && row.quantitySource === 'LENGTH_TIMES_COUNT' && count && lengthMm
        ? `${count} Stück á ${lengthMm} mm`
        : null;
    const rowNote = row.note && hasChainPlaceholder(row.note) ? fillPlaceholders(row.note, chain, issues) : row.note;
    const note = [cutNote, rowNote].filter(Boolean).join(' · ') || null;
    const description = hasChainPlaceholder(row.description)
      ? fillPlaceholders(row.description, chain, issues)
      : row.description;
    /**
     * Der ARTIKEL dieser Zeile — bei einem Baugruppen-Verweis (`assemblyKey` +
     * `layoutTemplateKey`, ADR 0097) kommt er aus der Auslegung, nicht aus der
     * Vorlage: Die Zeile beschreibt eine ROLLE („Hauptwelle"), der Artikel dahinter
     * ist je Projekt ein anderer. Ist in der Auslegung noch keiner gewählt, bleibt
     * er leer — geraten wird nicht, gemeldet schon.
     */
    const layoutArticle = isPosition ? layoutRowOf(row) : null;
    if (isPosition && row.layoutTemplateKey && !layoutArticle?.articleId) {
      issues.push(
        `${row.description}: Artikel käme aus der Auslegungszeile „${row.layoutTemplateKey}" — ` +
          'dort ist noch keiner gewählt.',
      );
    }
    const layoutUnit = articleUnitSchema.safeParse(layoutArticle?.unit).success
      ? (layoutArticle?.unit as ArticleUnit)
      : null;
    items.push({
      key: `vorlage-${row.position}`,
      id: null,
      parentKey: parentKeyOf(row),
      type: row.type,
      description,
      // `??` wäre hier falsch: Eine gefundene Auslegungszeile OHNE Artikel
      // (Platzhalter) muss `null` bleiben, nicht auf den Katalog-Artikel
      // zurückfallen — sonst verschwindet die Meldung von oben wortlos wieder.
      articleId: isPosition ? (layoutArticle ? layoutArticle.articleId : row.articleId) : null,
      articleNumber: isPosition ? (layoutArticle ? layoutArticle.articleNumber : row.articleNumber) : null,
      quantity: isPosition ? (quantities.get(row.position) ?? null) : null,
      // Bei Meterware die Stückzahl mitgeben: sie treibt später das
      // Befestigungsmaterial (12 Balken = 24 Schrauben, nicht 142).
      pieceCount: isPosition && row.quantitySource === 'LENGTH_TIMES_COUNT' ? count : null,
      unit: isPosition ? (layoutUnit ?? row.unit) : null,
      materialCode: null,
      materialText: isPosition ? row.materialText : null,
      dimensions: isPosition && lengthMm ? `${lengthMm} mm` : null,
      groupCode: isPosition ? row.groupCode : null,
      supplierId: null,
      supplierName: isPosition ? row.supplierName : null,
      note,
    });
  }

  return {
    items,
    issues,
    summary: {
      taken: items.filter((item) => item.type === 'POSITION').length,
      skipped: ordered.filter((row) => row.type === 'POSITION' && !survives.get(row.position)).length,
      sections: items.filter((item) => item.type === 'SECTION').length,
    },
  };
}
